import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { Flex, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { routePaths } from 'router';
import type { InfaktSyncRes, InvoiceRes } from 'api/swagger';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import {
  formatPriceInDefault,
  formatPriceInOriginalCurrency,
  isDefaultCurrency,
} from 'shared/utils/currency/format-price.ts';
import { InvoiceStatus } from 'types/invoice-status';
import { InvoiceCheckbox } from 'components/invoices/invoice-checkbox';
import { InvoiceStatusTag } from 'components/invoices/invoice-status-tag';
import { InvoiceDates } from 'components/invoices/invoices-list/components/invoice-dates';
import { InvoiceDownloadButton } from 'components/invoices/invoices-list/components/invoice-download-button';
import { INFAKT_SYNC_ICON } from 'components/invoices/invoices-list/constants';
import type { IInvoicesListProps } from 'components/invoices/invoices-list/types';
import styles from './invoices-list.module.scss';

export const InvoicesList: React.FC<IInvoicesListProps> = (props) => {
  const { invoices, isLoading, withoutClient = false, withCheckbox = false } = props;
  const { t } = useTranslation('invoice');

  const tableData = useTransformToTableData(invoices);

  const columns: ColumnsType<InvoiceRes> = [
    ...(withCheckbox
      ? [
          {
            title: '',
            dataIndex: 'id',
            width: '2%',
            className: styles.checkboxCol,
            render: (_: number, record: InvoiceRes) => <InvoiceCheckbox invoiceId={record.id} />,
          },
        ]
      : []),
    {
      title: 'Order · Client №',
      key: 'order',
      width: '10%',
      render: (_, { id, order, trip, warehouseTask }: InvoiceRes) => {
        const related = order || trip || warehouseTask;
        const clientOrderNumber = order ? order.clientOrderNumber : !trip ? warehouseTask?.clientOrderNumber : null;

        return (
          <Flex vertical>
            {related ? (
              <Link to={generatePath(routePaths.invoice, { invoiceId: String(id) })}>
                <strong>{related.compositeId}</strong>
              </Link>
            ) : (
              '—'
            )}
            <Typography.Text className={styles.secondary}>{clientOrderNumber || '—'}</Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: withoutClient ? 'Invoice ID' : 'Client · Invoice ID',
      key: 'clientInvoice',
      width: '24%',
      render: (_, { id, client, main }: InvoiceRes) => (
        <Flex vertical>
          {!withoutClient && (
            <Link to={generatePath(routePaths.client, { clientId: String(client.id) })} className={styles.clientLink}>
              {client.shortName || client.name}
            </Link>
          )}
          <Flex gap='small' align='center' className={styles.secondary}>
            Invoice #{id}
            {main ? <ExclamationCircleOutlined /> : null}
          </Flex>
        </Flex>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '9%',
      render: (status, { partiallyPaidAmount }: InvoiceRes) => (
        <>
          <InvoiceStatusTag status={status} partiallyPaidAmount={partiallyPaidAmount} />
        </>
      ),
    },
    {
      title: 'Infakt',
      dataIndex: 'infaktSync',
      width: '10%',
      render: (infaktSync?: InfaktSyncRes) => {
        const status = infaktSync?.status;
        const { icon, color } = status ? INFAKT_SYNC_ICON[status] : { icon: <MinusCircleOutlined />, color: 'gray' };

        return (
          <Flex align='center'>
            <Typography.Text style={{ color }}>{icon}</Typography.Text>
            <Typography.Text className={styles.syncStatus} style={{ color }}>
              {t(`infakt_label_${status || 'not_synced'}`)}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: 'Total · Price + VAT',
      dataIndex: 'totalSum',
      width: '22%',
      render: (totalSum, { currency, totalSumInDefault, price, vatPercent, vatSum }: InvoiceRes) => (
        <>
          <Flex gap='small' align='center' wrap>
            <Typography.Text className={styles.mainPrice} strong>
              {formatPriceInDefault(totalSum, totalSumInDefault, currency)}
            </Typography.Text>
            {!isDefaultCurrency(currency) && (
              <Typography.Text type='secondary' className={styles.secondaryPrice}>
                {formatPriceInOriginalCurrency(totalSum, currency)}
              </Typography.Text>
            )}
          </Flex>
          <Typography.Text type='secondary' className={styles.secondaryPrice}>
            {formatPriceInOriginalCurrency(price, currency)}
            {vatPercent ? ` + ${vatPercent}% VAT (${formatPriceInOriginalCurrency(vatSum, currency)})` : ' · no VAT'}
          </Typography.Text>
        </>
      ),
    },
    {
      title: 'Dates',
      key: 'dates',
      width: '16%',
      render: (_, { startPaymentPeriod, endPaymentPeriod, completedDate, status }: InvoiceRes) => (
        <InvoiceDates
          startPaymentPeriod={startPaymentPeriod}
          endPaymentPeriod={endPaymentPeriod}
          completedDate={completedDate}
          status={status}
        />
      ),
    },
    {
      key: 'download',
      width: 48,
      align: 'center',
      render: (_, invoice: InvoiceRes) => <InvoiceDownloadButton invoice={invoice} />,
    },
  ];

  return (
    <div>
      <Table
        rowClassName={(record) => (record.status === InvoiceStatus.Cancelled ? styles.canceledRow : '')}
        size={'small'}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        loading={isLoading ? { size: 'large', delay: 200 } : false}
      />
    </div>
  );
};
